<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSwitch from 'dashboard/components-next/switch/Switch.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from './SectionLayout.vue';

const DEFAULT_LABEL_TITLE = 'iniciado-pelo-agente';

const store = useStore();
const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();
const labels = useMapGetter('labels/getLabels');
const labelUiFlags = useMapGetter('labels/getUIFlags');

const enabled = ref(false);
const labelId = ref('');
const isSaving = ref(false);
const initializedAccountId = ref(null);

const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.id, label: label.title }))
);

const selectedLabelIsInvalid = computed(
  () =>
    !labelUiFlags.value.isFetching &&
    !!labelId.value &&
    !labels.value.some(label => label.id === Number(labelId.value))
);

const canSave = computed(
  () =>
    !isSaving.value &&
    (!enabled.value || (!!labelId.value && !selectedLabelIsInvalid.value))
);

const initialize = account => {
  if (!account?.id) return;
  const settings = account.settings || {};
  enabled.value = !!settings.agent_initiated_conversation_enabled;
  labelId.value = settings.agent_initiated_conversation_label_id || '';
  initializedAccountId.value = account.id;
};

watch(
  currentAccount,
  account => {
    if (account?.id !== initializedAccountId.value) initialize(account);
  },
  { immediate: true }
);

const save = async () => {
  if (!canSave.value) return;

  isSaving.value = true;
  try {
    await updateAccount(
      {
        agent_initiated_conversation_enabled: enabled.value,
        agent_initiated_conversation_label_id:
          labelId.value === '' ? null : Number(labelId.value),
      },
      { silent: true }
    );
    useAlert(
      t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.API.SUCCESS')
    );
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const createDefaultLabel = async () => {
  const existingLabel = labels.value.find(
    label => label.title === DEFAULT_LABEL_TITLE
  );
  if (existingLabel) {
    labelId.value = existingLabel.id;
    return;
  }

  try {
    await store.dispatch('labels/create', {
      title: DEFAULT_LABEL_TITLE,
      description: t(
        'GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.DEFAULT_LABEL_DESCRIPTION'
      ),
      color: '#6B7CFF',
      show_on_sidebar: true,
    });
  } catch {
    await store.dispatch('labels/get');
  }

  const createdLabel = labels.value.find(
    label => label.title === DEFAULT_LABEL_TITLE
  );
  if (createdLabel) {
    labelId.value = createdLabel.id;
    useAlert(
      t(
        'GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.CREATE_LABEL_SUCCESS'
      )
    );
  } else {
    useAlert(
      t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.CREATE_LABEL_ERROR')
    );
  }
};

onMounted(() => {
  if (!labels.value.length) store.dispatch('labels/get');
});
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.NOTE')"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <NextSwitch v-model="enabled" />
      </div>
    </template>

    <div class="grid gap-4">
      <WithLabel
        name="agent-initiated-conversation-label"
        :label="t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.LABEL')"
      >
        <ComboBox
          v-model="labelId"
          :options="labelOptions"
          :disabled="!enabled"
          :placeholder="
            t(
              'GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.LABEL_PLACEHOLDER'
            )
          "
          :search-placeholder="
            t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.LABEL_SEARCH')
          "
          :empty-state="
            t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.LABEL_EMPTY')
          "
          :display-label="
            selectedLabelIsInvalid
              ? t(
                  'GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.INVALID_LABEL',
                  { id: labelId }
                )
              : ''
          "
          :has-error="selectedLabelIsInvalid"
        />
      </WithLabel>

      <p
        v-if="selectedLabelIsInvalid"
        class="p-3 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{
          t(
            'GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.INVALID_LABEL_WARNING',
            { id: labelId }
          )
        }}
      </p>

      <div class="flex flex-wrap justify-between gap-3">
        <NextButton
          type="button"
          faded
          slate
          :disabled="!enabled || labelUiFlags.isCreating"
          :is-loading="labelUiFlags.isCreating"
          :label="
            t(
              'GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.CREATE_DEFAULT_LABEL'
            )
          "
          @click="createDefaultLabel"
        />
        <NextButton
          type="button"
          :disabled="!canSave"
          :is-loading="isSaving"
          :label="t('GENERAL_SETTINGS.FORM.AGENT_INITIATED_CONVERSATION.SAVE')"
          @click="save"
        />
      </div>
    </div>
  </SectionLayout>
</template>
